/* eslint-disable no-console */
import { ClientTextureUtils } from '../cl-texture-utils';

import {
  assertGlNoError,
  CreateGlTextureByPixels,
  CreateShaderProgram,
  CreateTextureBoundFrameBuffer,
  DeleteFramebuffer,
  EnableVertexAttributeArray,
  SetVertexAttribute,
  UnBindFrameBuffer,
  UnBindTexture
} from './utils/pipeline-utils-gl';

// TODO: Get rid of this. and replace sets of uniform params with union type,
// even if it means more type checking in inherited classes, better than resorting to "any" usage.
export type BaseUniformData = any;
export type ProgramProps = { [name: string]: any };
export type BufferSet = { [name: string]: ShaderPropBufferData };

const VERT_POS_BUFFER_NAME = 'a_vertPos';
const VERT_INDICIES_BUFFER_NAME = 'coordIndicies';

export interface ShaderPropBufferData {
  buffer: WebGLBuffer | null;
  itemSize: number;
  itemCount: number;
}

export class BasePipeline {
  private readonly DEFAULT_CANVAS_DIMENSION: number = 100;

  // TODO: I should be using get accessors for these straight from the canvas reference, and nothing more.
  public canvasWidth: number;
  public canvasHeight: number;

  public vertShader: string;
  public fragShader: string;

  public shaderProgram?: WebGLProgram;
  public shaderProgramProps: ProgramProps;
  public buffers: BufferSet;

  protected canvas?: HTMLCanvasElement;
  protected gl!: WebGLRenderingContext;

  public externalContext: boolean;

  public constructor(vertShader: string, fragShader: string) {
    this.canvasWidth = this.DEFAULT_CANVAS_DIMENSION;
    this.canvasHeight = this.DEFAULT_CANVAS_DIMENSION;

    this.vertShader = vertShader;
    this.fragShader = fragShader;

    this.shaderProgramProps = {};
    this.buffers = {};
    this.externalContext = false;
  }

  public get Gl(): WebGLRenderingContext {
    return this.gl;
  }

  public Init(canvasWidth: number, canvasHeight: number, gl?: WebGLRenderingContext): void {
    // * Already init
    if (this.gl) { return; }

    // console.log(`BasePipeline<${this.constructor.name}>::Init: gl instance provided <${!!gl}>, has context? <${!gl?.isContextLost()}>`);

    if (gl) {
      this.gl = gl;
      this.externalContext = true;
    }
    else {
      const { canvas, gl: newGl } = ClientTextureUtils.CreateCanvasWebGL(canvasWidth, canvasHeight);
      this.canvas = canvas;
      this.gl = newGl;
      this.externalContext = false;
    }

    this.SetDimensions(canvasWidth, canvasHeight);

    if (this.gl.isContextLost()) {
      throw new Error(`BasePipeline::Init: GL context lost before pipeline <${this.constructor.name}> could initialize.`);
    }

    this.OnContextAcquired();

    this.shaderProgram = this.CreateShaderProgram();
    this.UseProgram();
    this.InitShaderProps(this.shaderProgram);
    this.InitBuffers();
  }

  // overridden in subsclass
  protected OnContextAcquired(): void {
    // does stuff in subclasses
  }

  public SetDimensions(canvasWidth: number, canvasHeight: number): void {
    // ! In most cases, as the gl type would indicate, canvas is a non-optional property and always available.
    // - The "avatar service" however, runs independent "gl" and "canvas" modules, and in that case,
    // the "gl" object does not have any "canvas" reference. Hence, this check should appropriately fail.
    if (this.gl.canvas) {
      this.gl.canvas.width = canvasWidth;
      this.gl.canvas.height = canvasHeight;
    }
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
  }

  public Destroy(): void {
    if (!this.gl) {
      return;
    }

    this.DestroyBuffers();
    this.DestroyShaderProgram();
    this.canvas?.remove();

    if (!this.externalContext) {
      this.gl.getExtension('WEBGL_lose_context')?.loseContext();
    }

    this.shaderProgram = undefined;
    this.gl = undefined!;
  }

  public DestroyBuffers(): void {
    for (const key in this.buffers) {
      if (!Object.prototype.hasOwnProperty.call(this.buffers, key)) { continue; }
      const bufferSet: ShaderPropBufferData = this.buffers[key];
      if (!bufferSet || bufferSet.buffer === null) { throw new Error('Could not access buffer.'); }
      const buffer: WebGLBuffer = bufferSet.buffer;
      this.gl.deleteBuffer(buffer);
      delete this.buffers[key];
    }
  }

  public DestroyShaderProgram(): void {
    this.shaderProgram && this.gl.deleteProgram(this.shaderProgram);
    for (const key in this.shaderProgramProps) {
      if (!Object.prototype.hasOwnProperty.call(this.shaderProgramProps, key)) { continue; }
      delete this.shaderProgramProps[key];
    }
  }

  protected CreateShaderProgram(): WebGLProgram {
    return CreateShaderProgram(this.gl, this.vertShader, this.fragShader);
  }

  public UseProgram(): void {
    if (!this.shaderProgram) {
      throw new Error(`BasePipeline::UseProgram: No shader program to use.`);
    }

    if (this.gl.isContextLost()) {
      throw new Error(`BasePipeline::UseProgram: GL context lost in pipeline <${this.constructor.name}>.`);
    }

    const deleteStatus = this.gl.getProgramParameter(this.shaderProgram, this.gl.DELETE_STATUS);
    if (deleteStatus) {
      throw new Error(`- deleteStatus true while attempting to use program...? GL context active <${!this.gl.isContextLost()}>`);
    }

    this.gl.useProgram(this.shaderProgram);
  }

  protected InitShaderProps(shaderProgram: WebGLProgram): void {
    this.shaderProgramProps[VERT_POS_BUFFER_NAME] = EnableVertexAttributeArray(
      this.gl,
      shaderProgram,
      'inPosition',
      this.constructor.name
    );
  }

  protected InitBuffers(): void {
    this.initVertexPositionBuffer();
    this.initializeVertexIndiciesBuffer();
  }

  public initVertexPositionBuffer(): void {
    // Simply defining coords, not yet in order of tris
    const planeCoords = [
      -1.0, 1.0, // TL
      -1.0, -1.0, // BL
      1.0, -1.0, // BR
      1.0, 1.0 // TR
    ];

    this.buffers[VERT_POS_BUFFER_NAME] = {
      buffer: this.gl.createBuffer(),
      itemSize: 2,
      itemCount: planeCoords.length / 2
    };

    const bufferData: ShaderPropBufferData = this.buffers[VERT_POS_BUFFER_NAME];
    if (!bufferData || bufferData.buffer === null) { throw new Error(`Buffer data for ${VERT_POS_BUFFER_NAME} does not exist.`); }

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, bufferData.buffer);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(planeCoords), this.gl.STATIC_DRAW);
  }

  // VERTEX INDICES (Order of triangle draw calls, counter-clockwise if I recall)
  public initializeVertexIndiciesBuffer(): void {
    // Defining order of tris.
    const PLANE_INDICIES: number[] = [
      0, 1, 3,
      3, 1, 2
    ];
    this.buffers[VERT_INDICIES_BUFFER_NAME] = {
      buffer: this.gl.createBuffer(),
      itemSize: 1,
      itemCount: PLANE_INDICIES.length
    };
    const coordInsidicesBuffer: ShaderPropBufferData = this.buffers[VERT_INDICIES_BUFFER_NAME];
    if (!coordInsidicesBuffer || coordInsidicesBuffer.buffer === null) { throw new Error(`coordIndicies buffer data does not exist.`); }

    this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, coordInsidicesBuffer.buffer);
    this.gl.bufferData(this.gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(PLANE_INDICIES), this.gl.STATIC_DRAW);
    // console.log(`Vert position and coord index buffers added, this.buffers:`, this.buffers);
  }

  protected SetVertexAttributes(): void {
    this.SetVertexAttribute(VERT_POS_BUFFER_NAME);
  }

  protected SetVertexAttribute(attrPropName: string): void {
    const bufferSet = this.buffers[attrPropName];
    const programProps = this.shaderProgramProps[attrPropName];
    if (!bufferSet || programProps === undefined || programProps === null) {
      console.error(`Pipeline <${this.constructor.name}>, attribute prop name <${attrPropName}> ` +
        `not found in buffers or shaderProgramProps:`, this.buffers, this.shaderProgramProps);
      throw new Error(`SetVertexAttribute error description above`);
    }

    SetVertexAttribute(this.gl, bufferSet.buffer, programProps, bufferSet.itemSize);
  }

  protected SetUniforms(data: BaseUniformData): void {}

  public ClearDrawBuffer(): void {
    this.gl.clearColor(0, 0, 0, 0);
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);
  }

  public Draw(uniformData: BaseUniformData): void {
    this.gl.viewport(0, 0, this.canvasWidth, this.canvasHeight);
    this.ClearDrawBuffer();
    this.SetVertexAttributes();
    this.SetUniforms(uniformData);
    this.DrawTrisFromVertIndices();
  }

  /**
   * Creates an empty texture, binding it to a frame buffer that becomes
   * the new draw source, then does a normal draw to that buffer, thus populating the texture.
   * @param uniformData
   * @returns
   */
  public DrawToTexture(uniformData: BaseUniformData): WebGLTexture {
    if (!this.gl) {
      console.trace('DrawToTexture Error Trace');
      throw new Error(`gl must be defined in DrawToTexture(), but it was ${this.gl}`);
    }
    const tex = CreateGlTextureByPixels(this.gl, this.canvasWidth, this.canvasHeight, null);
    const frameBuffer = CreateTextureBoundFrameBuffer(this.gl, tex);

    try {
      this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, frameBuffer);
      this.gl.bindTexture(this.gl.TEXTURE_2D, tex);
      this.Draw(uniformData);
    }
    finally {
      UnBindFrameBuffer(this.gl);
      UnBindTexture(this.gl);
      DeleteFramebuffer(this.gl, frameBuffer);
    }

    return tex;
  }

  /**
   * Does a normal draw, that acquires pixel data, then sends that into a new texture object to be returned.
   * @param uniformData
   * @returns
   */
  public DrawToTexture2(uniformData: BaseUniformData): WebGLTexture {
    this.Draw(uniformData);

    const pixelData = this.GetDrawnPixelData();
    return CreateGlTextureByPixels(this.gl, this.canvasWidth, this.canvasHeight, pixelData);
  }

  public DrawOnceForPixelData(uniformData: BaseUniformData = {}): Uint8Array {
    this.Draw(uniformData);
    return this.GetDrawnPixelData();
  }

  public DrawTrisFromVertIndices(): void {
    const bufferHolder: ShaderPropBufferData = this.buffers[VERT_INDICIES_BUFFER_NAME];
    if (!bufferHolder) { throw new Error(`Expected buffer to exist for "${VERT_INDICIES_BUFFER_NAME}". But it was ${bufferHolder}.`); }

    this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, bufferHolder.buffer);
    this.gl.drawElements(this.gl.TRIANGLES, bufferHolder.itemCount, this.gl.UNSIGNED_SHORT, 0);
    this.gl.flush();
    assertGlNoError(this.gl, `drawElements (${this.constructor.name})`);
  }

  public GetDrawnPixelData(): Uint8Array {
    if (!this.canvasWidth || !this.canvasHeight) {
      throw new Error(`BasePipeline::GetDrawnPixelData: Canvas width and height required in order to read the pixel data.`);
    }

    const pixelData = new Uint8Array(this.canvasWidth * this.canvasHeight * 4);
    this.gl.readPixels(0, 0, this.canvasWidth, this.canvasHeight, this.gl.RGBA, this.gl.UNSIGNED_BYTE, pixelData);
    assertGlNoError(this.gl, `readPixels (${this.canvasWidth}x${this.canvasHeight})`);
    return pixelData;
  }

  public GetTexturePixelData(texture: WebGLTexture): Uint8Array {
    if (!this.canvasWidth || !this.canvasHeight) {
      throw new Error(`BasePipeline::GetTexturePixelData: Canvas width and height required in order to read the pixel data.`);
    }

    const pixelData = new Uint8Array(this.canvasWidth * this.canvasHeight * 4);

    // here we use a framebuffer as an offscreen render object
    // draw the texture into it and then copy the pixel values into a local array.
    const framebuffer = this.gl.createFramebuffer();
    if (!framebuffer) {
      throw new Error(`BasePipeline::GetTexturePixelData: Could not create framebuffer`);
    }

    try {
      this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, framebuffer);
      this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.COLOR_ATTACHMENT0, this.gl.TEXTURE_2D, texture, 0);

      const status = this.gl.checkFramebufferStatus(this.gl.FRAMEBUFFER);
      if (status !== this.gl.FRAMEBUFFER_COMPLETE) {
        throw new Error(`BasePipeline::GetTexturePixelData: Framebuffer incomplete, status <${status}>`);
      }

      this.gl.readPixels(0, 0, this.canvasWidth, this.canvasHeight, this.gl.RGBA, this.gl.UNSIGNED_BYTE, pixelData);
      assertGlNoError(this.gl, `readPixels from texture (${this.canvasWidth}x${this.canvasHeight})`);
    }
    finally {
      this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);
      DeleteFramebuffer(this.gl, framebuffer);
    }

    return pixelData;
  }

  public CleanGlState(): void {
    if (!this.gl) {
      return;
    }

    this.ClearDrawBuffer();

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, null);
    this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, null);
    this.gl.bindRenderbuffer(this.gl.RENDERBUFFER, null);
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);
  }
}
